import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {useParams} from 'react-router';

import {useRampFlagIssuesObservability} from '../../util/rampFeatureFlags';
import {Run} from '../../util/runTypes';
import {DUMMY_ISSUES} from './DUMMY_ISSUES';
import {
  initialState,
  issuesByTimestampReducer,
} from './issuesByTimestampReducer';
import {doTimestampsByMetricQuery} from './timestampQuery';
import {Issue, IssueLine, TimestampKeyTuple} from './types';

type IssuesContextType = {
  activeIssues: Issue[];
  activeRun?: Run | null;
  annotationActive: boolean;
  currentIssue: string | null;
  fetchTimestamps: ({
    xAxis,
    zoom,
  }: {
    xAxis: string;
    zoom: [number | null, number | null];
  }) => Promise<void>;
  // These are used to populate the panels
  getIssuesByCacheKey: (cacheKey: string) => IssueLine[];
  // this is used to populate the drawer
  getIssuesByRunId: (runId: string) => Issue[];
  handleIssueInteraction: (
    event: 'hover' | 'unhover',
    issueId: string | null
  ) => () => void;
  iconPositions: {
    marginLeft: number;
    xMin: number;
    xMax: number;
  };
  issueRampStatus: 'enabled' | 'disabled';
  toggleIssues: () => void;
  updateIconPositions: (iconPositions: {
    marginLeft: number;
    xMin: number;
    xMax: number;
  }) => void;
  workspaceType: 'single-run' | 'multi-run' | 'group' | 'sweep' | null;
};

async function noOpFetch({
  xAxis,
  zoom,
}: {
  xAxis: string;
  zoom: [number | null, number | null];
}) {}

function noOpGetIssuesByCacheKey() {
  return [];
}

function noOpGetIssuesByRunId() {
  return [];
}

export const IssuesContext = React.createContext<IssuesContextType>({
  activeIssues: [],
  activeRun: null,
  annotationActive: true,
  currentIssue: null,
  fetchTimestamps: noOpFetch,
  getIssuesByCacheKey: noOpGetIssuesByCacheKey,
  getIssuesByRunId: noOpGetIssuesByRunId,
  handleIssueInteraction: () => () => {},
  iconPositions: {
    marginLeft: 0,
    xMin: 0,
    xMax: 0,
  },
  issueRampStatus: 'disabled',
  updateIconPositions: () => {},
  toggleIssues: () => {},
  workspaceType: null,
});

function convertZoomToCacheKey(
  xAxis: string,
  zoom: [number | null, number | null]
) {
  return `${xAxis}-${zoom[0] ?? 'null'}-${zoom[1] ?? null}`;
}

export const IssuesContextProvider = ({
  children,
  run,
  workspaceType,
}: {
  children: React.ReactNode;
  run?: Run | null;
  workspaceType: 'single-run' | 'multi-run' | 'group' | 'sweep';
}) => {
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const canUseIssuesContext = useRampFlagIssuesObservability(
    entityName,
    projectName
  );
  const canFetch = workspaceType === 'single-run' && canUseIssuesContext;

  const [hoveredIssue, setHoveredIssue] = useState<string | null>(null);

  const handleIssueInteraction = useCallback(
    (event: 'hover' | 'unhover', issueId: string | null) => () => {
      if (event === 'hover') {
        setHoveredIssue(issueId);
      } else {
        setHoveredIssue(null);
      }
    },
    []
  );

  const [state, dispatch] = useReducer(issuesByTimestampReducer, initialState);
  const [useIssues, setUseIssues] = useState(true);
  const toggleIssues = useCallback(() => {
    setUseIssues(prev => !prev);
  }, []);
  const [iconPositions, setIconPositions] = useState({
    marginLeft: 0,
    xMin: 0,
    xMax: 0,
  });

  const updateIconPositions = useCallback(
    ({
      marginLeft,
      xMin,
      xMax,
    }: Partial<{
      marginLeft: number;
      xMin: number;
      xMax: number;
    }>) => {
      const canUpdate = [
        iconPositions.marginLeft !== marginLeft,
        iconPositions.xMin !== xMin,
        iconPositions.xMax !== xMax,
      ].some(condition => !!condition);

      if (canUpdate) {
        setIconPositions(prev => ({
          marginLeft: marginLeft ?? prev.marginLeft,
          xMin: xMin ?? prev.xMin,
          xMax: xMax ?? prev.xMax,
        }));
      }
    },
    [iconPositions]
  );

  const runName = run?.name;

  useEffect(() => {
    if (canUseIssuesContext && runName === 'mc6z6vup') {
      dispatch({
        type: 'addIssues',
        payload: {issues: DUMMY_ISSUES(['firing']), since: 0},
      });
    }
  }, [canUseIssuesContext, runName]);

  const fetchTimestamps = useCallback(
    async ({
      xAxis,
      zoom,
    }: {
      xAxis: string;
      zoom: [number | null, number | null];
    }) => {
      const cacheKey = convertZoomToCacheKey(xAxis, zoom);

      // prevent fetching any cache key zoom combo we already have or are currently fetching
      if (
        state.timestamps[cacheKey]?.keyByTimestamp?.length > 0 ||
        state.timestamps[cacheKey]?.loading ||
        !runName
      ) {
        return;
      }

      const propTarget = xAxis === '_step' ? 'Step' : 'X';
      const zoomObj: {
        minStep?: number;
        maxStep?: number;
        minX?: number;
        maxX?: number;
      } = {};
      if (zoom[0] != null) {
        zoomObj[`min${propTarget}`] = Math.floor(zoom[0]);
      }
      if (zoom[1] != null) {
        zoomObj[`max${propTarget}`] = Math.ceil(zoom[1]);
      }
      dispatch({type: 'fetchTimestamps-start', payload: {xAxis, cacheKey}});

      try {
        const {data} = await doTimestampsByMetricQuery({
          entityName,
          projectName,
          runName,
          bucketedHistorySpecs: [
            JSON.stringify({
              keys: ['_timestamp', xAxis],
              samples: 1000,
              bins: 1000,
              ...zoomObj,
              xAxis,
              metricTransformations: {},
            }),
          ],
        });
        const history = data.project?.run?.bucketedHistory;
        if (!history || history.length !== 1) {
          console.error(
            'Returned incorrect number of bucketedHistories from issuesTimestamp Query'
          );
          return;
        }
        const timestampTuples: TimestampKeyTuple[] = [];

        history[0]?.forEach((curr: any, index: number) => {
          const currentTimestampMs = curr._timestampLast * 1000;
          const currentXValue = curr[`${xAxis}Last`];
          if (currentTimestampMs == null || currentXValue == null) {
            console.error('Returned incorrect data from issuesTimestamp Query');
            return;
          }

          if (
            index > 0 &&
            index - 1 < timestampTuples.length &&
            timestampTuples[index - 1] &&
            (currentTimestampMs <= timestampTuples[index - 1][0] ||
              currentXValue <= timestampTuples[index - 1][1])
          ) {
            throw new Error(
              'Returned unsorted on nonmonotonic values from issuesTimestamp Query'
            );
          }

          // need to convert the timestamps coming back to mS
          timestampTuples.push([currentTimestampMs, currentXValue]);
        });

        dispatch({
          type: 'fetchTimestamps-success',
          payload: {xAxis, cacheKey, results: timestampTuples},
        });
      } catch (err) {
        dispatch({
          type: 'fetchTimestamps-error',
          payload: {xAxis, cacheKey, error: err as Error},
        });
      }
    },
    [state.timestamps, entityName, projectName, runName]
  );

  /**
   * Find the issues for a given cache key. If the cache isn't found, that means we're fetching and we should use the nearest matching zoom range temporarily.
   * NOTE: Extend this algorith to find the nearest match.
   */
  const getIssuesByCacheKey = useCallback(
    (cacheKey: string) => {
      if (!state.issuesByCacheKey[cacheKey]) {
        const [xAxis] = cacheKey.split('-');
        return state.issuesByCacheKey[`${xAxis}-null-null`] ?? [];
      }
      return state.issuesByCacheKey[cacheKey] ?? [];
    },
    [state.issuesByCacheKey]
  );

  const getIssuesByRunId = useCallback(
    (runId: string) => {
      return state.issuesByRunId[runId];
    },
    [state.issuesByRunId]
  );

  const disabledValue = useMemo(() => {
    return {
      activeIssues: [],
      activeRun: null,
      annotationActive: false,
      currentIssue: null,
      fetchTimestamps: noOpFetch,
      getIssuesByCacheKey: noOpGetIssuesByCacheKey,
      getIssuesByRunId: noOpGetIssuesByRunId,
      handleIssueInteraction: () => () => {},
      iconPositions: {
        marginLeft: 0,
        xMin: 0,
        xMax: 0,
      },
      issueRampStatus: 'disabled' as const,
      toggleIssues: () => {},
      updateIconPositions: () => {},
      workspaceType,
    };
  }, [workspaceType]);

  const val = useMemo(
    () => ({
      activeIssues: getIssuesByRunId(run?.name ?? ''),
      activeRun: run,
      annotationActive: useIssues,
      currentIssue: hoveredIssue,
      fetchTimestamps: canFetch ? fetchTimestamps : noOpFetch,
      getIssuesByCacheKey: canFetch
        ? getIssuesByCacheKey
        : noOpGetIssuesByCacheKey,
      getIssuesByRunId: canFetch ? getIssuesByRunId : noOpGetIssuesByRunId,
      handleIssueInteraction,
      iconPositions,
      issueRampStatus: canFetch ? ('enabled' as const) : ('disabled' as const),
      toggleIssues,
      updateIconPositions,
      workspaceType,
    }),
    [
      canFetch,
      fetchTimestamps,
      getIssuesByCacheKey,
      getIssuesByRunId,
      handleIssueInteraction,
      hoveredIssue,
      iconPositions,
      run,
      toggleIssues,
      updateIconPositions,
      useIssues,
      workspaceType,
    ]
  );

  return (
    <IssuesContext.Provider value={canUseIssuesContext ? val : disabledValue}>
      {children}
    </IssuesContext.Provider>
  );
};

export const useIssuesContext = () => {
  const val = React.useContext(IssuesContext);
  if (!val) {
    console.error(
      'useIssuesContext must be used within a IssuesContextProvider'
    );
  }
  return val;
};
